import { useEffect, useState, type FormEvent } from 'react';
import {
  IconCheck,
  IconKey,
  IconMail,
  IconPlugConnected,
  IconShieldCheck,
  IconTrash,
} from '@tabler/icons-react';
import {
  clearAdminSmtp,
  getAdminSmtp,
  setAdminSmtp,
  testAdminSmtp,
} from '../api/endpoints';
import { PageHeader } from '../components/AppShell';
import { ErrorNotice, LoadingRows } from '../components/Feedback';
import { Badge, Button, Card, Input } from '../components/ui';
import { formatRelative } from '../lib/format';
import { useAuth, useResource, useToast } from '../state';

export function AdminSmtpPage(): JSX.Element {
  const { user } = useAuth();
  const isAdmin = user?.role === 'admin';
  const toast = useToast();

  const smtpResource = useResource(() => getAdminSmtp(), [], {
    enabled: isAdmin,
  });

  const [host, setHost] = useState('');
  const [port, setPort] = useState<number>(587);
  const [secure, setSecure] = useState(false);
  const [username, setUsername] = useState('');
  const [password, setPassword] = useState('');
  const [from, setFrom] = useState('');
  const [enabled, setEnabled] = useState(true);

  const [isSaving, setIsSaving] = useState(false);
  const [isTesting, setIsTesting] = useState(false);
  const [isClearing, setIsClearing] = useState(false);
  const [testResult, setTestResult] = useState<{ ok: boolean; message: string } | null>(null);

  const smtpData = smtpResource.data?.smtp;

  useEffect(() => {
    if (smtpData && smtpData.configured) {
      setHost(smtpData.host ?? '');
      setPort(smtpData.port ?? 587);
      setSecure(Boolean(smtpData.secure));
      setUsername(smtpData.user ?? '');
      setFrom(smtpData.from ?? '');
      setEnabled(smtpData.enabled !== false);
    }
  }, [smtpData]);

  const handleSave = async (e: FormEvent) => {
    e.preventDefault();
    setIsSaving(true);
    setTestResult(null);
    try {
      await setAdminSmtp({
        host: host.trim(),
        port: Number(port),
        secure,
        user: username.trim() || undefined,
        pass: password || undefined,
        from: from.trim(),
        enabled,
      });
      toast.success('SMTP configuration saved successfully');
      setPassword('');
      smtpResource.reload();
    } catch (err) {
      toast.error(err instanceof Error ? err.message : 'Failed to save SMTP configuration');
    } finally {
      setIsSaving(false);
    }
  };

  const handleTestConnection = async () => {
    setIsTesting(true);
    setTestResult(null);
    try {
      const res = await testAdminSmtp({
        host: host.trim() || undefined,
        port: port ? Number(port) : undefined,
        secure,
      });
      setTestResult(res);
      toast.success('SMTP connection handshake verified!');
    } catch (err) {
      const msg = err instanceof Error ? err.message : 'SMTP connection failed';
      setTestResult({ ok: false, message: msg });
      toast.error(msg);
    } finally {
      setIsTesting(false);
    }
  };

  const handleClear = async () => {
    if (!confirm('Are you sure you want to reset and clear the SMTP settings?')) return;
    setIsClearing(true);
    setTestResult(null);
    try {
      await clearAdminSmtp();
      toast.success('SMTP configuration reset');
      setHost('');
      setPort(587);
      setSecure(false);
      setUsername('');
      setPassword('');
      setFrom('');
      setEnabled(true);
      smtpResource.reload();
    } catch (err) {
      toast.error(err instanceof Error ? err.message : 'Failed to reset SMTP settings');
    } finally {
      setIsClearing(false);
    }
  };

  if (!isAdmin) {
    return (
      <div className="page">
        <PageHeader title="Access Denied" subtitle="Super-admin authorization required." />
        <ErrorNotice error={new Error('Only operators with the admin role can configure system settings.')} />
      </div>
    );
  }

  return (
    <div className="page admin-smtp-page">
      <PageHeader
        title="System & SMTP Email Settings"
        subtitle="Configure the outgoing mail server for password resets, tenant failure alerts, and invite notifications."
      />

      <div className="grid gap-6 md:grid-cols-3">
        <div className="md:col-span-2">
          <Card className="p-6">
            <div className="mb-6 flex items-center justify-between border-b pb-4">
              <div>
                <h3 className="text-lg font-semibold tracking-tight">Outgoing Mail Server (SMTP)</h3>
                <p className="text-xs text-muted-foreground">
                  Connect your transaction email provider (Resend, SendGrid, Mailgun, Amazon SES, or custom Postfix).
                </p>
              </div>
              <div>
                {smtpData?.configured ? (
                  <Badge variant={smtpData.enabled ? 'default' : 'secondary'} className="flex items-center gap-1">
                    <IconCheck size={12} /> {smtpData.enabled ? 'Active' : 'Disabled'}
                  </Badge>
                ) : (
                  <Badge variant="outline" className="text-muted-foreground">
                    Not Configured
                  </Badge>
                )}
              </div>
            </div>

            {smtpResource.loading && !smtpResource.data ? (
              <LoadingRows rows={4} label="Loading settings..." />
            ) : (
              <form onSubmit={handleSave} className="space-y-4">
                <div className="grid grid-cols-3 gap-4">
                  <div className="col-span-2">
                    <label className="mb-1.5 block text-xs font-medium">SMTP Host / Server</label>
                    <Input
                      type="text"
                      required
                      placeholder="e.g. smtp.resend.com or smtp.mailgun.org"
                      value={host}
                      onChange={(e) => setHost(e.target.value)}
                    />
                  </div>

                  <div>
                    <label className="mb-1.5 block text-xs font-medium">Port</label>
                    <Input
                      type="number"
                      required
                      min="1"
                      max="65535"
                      placeholder="587"
                      value={port}
                      onChange={(e) => setPort(Number(e.target.value))}
                    />
                  </div>
                </div>

                <div className="flex items-center gap-2 rounded-md border border-input p-3 bg-muted/30">
                  <input
                    type="checkbox"
                    id="secureCheck"
                    checked={secure}
                    onChange={(e) => setSecure(e.target.checked)}
                    className="h-4 w-4 rounded border-input"
                  />
                  <label htmlFor="secureCheck" className="text-xs font-medium leading-none cursor-pointer">
                    Use SSL/TLS (Implicit TLS on Port 465). Uncheck for STARTTLS (Port 587 or 25).
                  </label>
                </div>

                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <label className="mb-1.5 block text-xs font-medium">Username / API Key</label>
                    <Input
                      type="text"
                      placeholder="e.g. resend or postmaster@..."
                      value={username}
                      onChange={(e) => setUsername(e.target.value)}
                    />
                  </div>

                  <div>
                    <label className="mb-1.5 block text-xs font-medium">Password / API Secret</label>
                    <Input
                      type="password"
                      placeholder={smtpData?.hasPassword ? '•••••••• (leave blank to keep current)' : 'Enter password'}
                      value={password}
                      onChange={(e) => setPassword(e.target.value)}
                    />
                    {smtpData?.hasPassword ? (
                      <span className="mt-1 block text-xs text-muted-foreground flex items-center gap-1">
                        <IconKey size={12} className="text-emerald-500" /> Password configured and securely stored
                      </span>
                    ) : null}
                  </div>
                </div>

                <div>
                  <label className="mb-1.5 block text-xs font-medium">Default "From" Address</label>
                  <Input
                    type="email"
                    required
                    placeholder="e.g. Justloop Verifier <noreply@justloop.vps.prakhya.id>"
                    value={from}
                    onChange={(e) => setFrom(e.target.value)}
                  />
                  <span className="mt-1 block text-xs text-muted-foreground">
                    Must be an authorized sender address with your email provider (SPF/DKIM).
                  </span>
                </div>

                <div className="flex items-center gap-2 pt-2">
                  <input
                    type="checkbox"
                    id="enableCheck"
                    checked={enabled}
                    onChange={(e) => setEnabled(e.target.checked)}
                    className="h-4 w-4 rounded border-input"
                  />
                  <label htmlFor="enableCheck" className="text-xs font-medium leading-none cursor-pointer">
                    Enable active email dispatching
                  </label>
                </div>

                {testResult ? (
                  <div
                    className={`rounded-md p-3 text-xs flex items-start gap-2 ${
                      testResult.ok ? 'bg-emerald-500/10 text-emerald-700 dark:text-emerald-400' : 'bg-destructive/10 text-destructive'
                    }`}
                  >
                    {testResult.ok ? <IconShieldCheck size={16} className="mt-0.5 shrink-0" /> : <IconMail size={16} className="mt-0.5 shrink-0" />}
                    <div>
                      <strong>{testResult.ok ? 'Connection Verified: ' : 'Connection Failed: '}</strong>
                      {testResult.message}
                    </div>
                  </div>
                ) : null}

                <div className="flex flex-wrap items-center justify-between gap-3 pt-4 border-t">
                  <div className="flex items-center gap-2">
                    <Button type="submit" disabled={isSaving}>
                      {isSaving ? 'Saving...' : 'Save Configuration'}
                    </Button>
                    <Button
                      type="button"
                      variant="outline"
                      onClick={handleTestConnection}
                      disabled={isTesting || !host}
                      className="flex items-center gap-1.5"
                    >
                      <IconPlugConnected size={14} />
                      {isTesting ? 'Testing Handshake...' : 'Test Handshake'}
                    </Button>
                  </div>

                  {smtpData?.configured ? (
                    <Button
                      type="button"
                      variant="destructive"
                      size="sm"
                      onClick={handleClear}
                      disabled={isClearing}
                      className="flex items-center gap-1.5"
                    >
                      <IconTrash size={14} /> Reset
                    </Button>
                  ) : null}
                </div>
              </form>
            )}
          </Card>
        </div>

        <div>
          <Card className="p-5 space-y-4">
            <h4 className="text-sm font-semibold tracking-tight">Email System Status</h4>
            <div className="space-y-2 text-xs text-muted-foreground">
              <div className="flex justify-between py-1 border-b">
                <span>Transport Status:</span>
                <span className="font-medium text-foreground">
                  {smtpData?.configured ? (smtpData.enabled ? 'Enabled' : 'Disabled') : 'Simulated / Log'}
                </span>
              </div>
              <div className="flex justify-between py-1 border-b">
                <span>Configured Host:</span>
                <span className="font-medium text-foreground">{smtpData?.host ?? '—'}</span>
              </div>
              <div className="flex justify-between py-1 border-b">
                <span>Port / TLS:</span>
                <span className="font-medium text-foreground">
                  {smtpData?.port ? `${smtpData.port} (${smtpData.secure ? 'SSL' : 'STARTTLS'})` : '—'}
                </span>
              </div>
              <div className="flex justify-between py-1 border-b">
                <span>Sender Origin:</span>
                <span className="font-medium text-foreground">{smtpData?.from ?? '—'}</span>
              </div>
              <div className="flex justify-between py-1">
                <span>Last Updated:</span>
                <span className="font-medium text-foreground">
                  {smtpData?.updatedAt ? formatRelative(smtpData.updatedAt) : 'Never'}
                </span>
              </div>
            </div>

            <div className="rounded-md bg-muted/50 p-3 text-xs space-y-2 text-muted-foreground">
              <p className="font-semibold text-foreground">Security Guarantee:</p>
              <p>
                Credentials are encrypted and isolated in the operator setting store. Password values are never exposed across the network.
              </p>
            </div>
          </Card>
        </div>
      </div>
    </div>
  );
}
